/**
 * CircleView — main page for the "Meu Círculo" tab.
 * Replaces Shop.jsx. Route: /circle
 *
 * Sections:
 *  1. Jardim visual — grid of member acacia trees
 *  2. Circle name + member list
 *  3. My resources (ResourceBar expanded)
 *  4. Eco feed (last 5 this week)
 *  5. Collective garden progress bar
 *  6. Actions: offer watering / send fertilizer
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import LoadingAnimation from "./LoadingAnimation";
import AcaciaTree from "./AcaciaTree";
import ResourceBar from "./ResourceBar";
import EcoFeed from "./EcoFeed";
import CircleNaming from "./CircleNaming";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_THRESHOLDS = [0, 5, 15, 35, 60];
function stageFromPoints(pts) {
  let s = 0;
  for (let i = 0; i < STAGE_THRESHOLDS.length; i++) {
    if (pts >= STAGE_THRESHOLDS[i]) s = i;
  }
  return s;
}

function collectiveStageFromPts(pts) {
  if (pts >= 100) return 4;
  if (pts >= 60) return 3;
  if (pts >= 30) return 2;
  if (pts >= 10) return 1;
  return 0;
}

export default function CircleView() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [circle, setCircle] = useState(null);
  const [garden, setGarden] = useState(null);
  const [echos, setEchos] = useState([]);
  const [echoLoading, setEchoLoading] = useState(false);
  const [resources, setResources] = useState(null);
  const [circleList, setCircleList] = useState([]);
  const [showNaming, setShowNaming] = useState(false);

  // Watering modal state
  const [showWaterModal, setShowWaterModal] = useState(false);
  const [wateringTarget, setWateringTarget] = useState(null);
  const [watering, setWatering] = useState(false);

  // Fertilizer modal state
  const [showFertModal, setShowFertModal] = useState(false);
  const [fertTarget, setFertTarget] = useState(null);
  const [fertilizing, setFertilizing] = useState(false);

  // Self-actions state
  const [selfWatering, setSelfWatering] = useState(false);
  const [selfFertilizing, setSelfFertilizing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [circleRes, resRes] = await Promise.all([
        axios.get(`${API}/circles/my-circle`, { withCredentials: true }),
        axios.get(`${API}/gamification/my-resources`, { withCredentials: true }),
      ]);

      const circleData = circleRes.data;
      setCircle(circleData.circle || null);
      setResources(resRes.data);

      if (circleData.circle?.circle_id) {
        const cid = circleData.circle.circle_id;

        setEchoLoading(true);
        const [gardenRes, echoRes] = await Promise.all([
          axios.get(`${API}/circles/${cid}/garden`, { withCredentials: true }),
          axios.get(`${API}/circles/${cid}/echos`, { withCredentials: true }),
        ]);
        setGarden(gardenRes.data);
        setEchos(echoRes.data || []);
        setEchoLoading(false);

        // If naming/objection status, show modal
        if (circleData.circle.status === "naming") {
          setShowNaming(true);
        }
      }
    } catch (err) {
      console.error("CircleView fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function fetchCircleList() {
    try {
      const { data } = await axios.get(`${API}/eco/circle-list`, { withCredentials: true });
      setCircleList(data.circles || []);
    } catch {
      setCircleList([]);
    }
  }

  async function handleWater() {
    if (!wateringTarget) return;
    setWatering(true);
    try {
      await axios.post(
        `${API}/eco/water`,
        { recipient_user_id: wateringTarget.user_id },
        { withCredentials: true }
      );
      toast({
        title: "Rega enviada! 💧",
        description: `A acácia de ${wateringTarget.name} ganhou +1 ponto. Você usou 1 regador e 1 rega.`,
      });
      setShowWaterModal(false);
      setWateringTarget(null);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao enviar rega.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setWatering(false);
    }
  }

  async function handleFertilize() {
    if (!fertTarget) return;
    setFertilizing(true);
    try {
      await axios.post(
        `${API}/eco/fertilize`,
        { target_circle_id: fertTarget.circle_id },
        { withCredentials: true }
      );
      toast({
        title: "Adubo enviado! 🌿",
        description: `O jardim de "${fertTarget.name || 'outro círculo'}" ganhou +3 pontos coletivos. Você usou 1 adubo.`,
      });
      setShowFertModal(false);
      setFertTarget(null);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao enviar adubo.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setFertilizing(false);
    }
  }

  async function handleSelfWater() {
    setSelfWatering(true);
    try {
      await axios.post(`${API}/eco/self-water`, {}, { withCredentials: true });
      toast({ title: "Acácia regada!", description: "Sua acácia ganhou +1 ponto 💧" });
      fetchData();
    } catch (err) {
      toast({ title: "Erro", description: err.response?.data?.detail || "Erro ao regar.", variant: "destructive" });
    } finally {
      setSelfWatering(false);
    }
  }

  async function handleSelfFertilize() {
    setSelfFertilizing(true);
    try {
      await axios.post(`${API}/eco/self-fertilize`, {}, { withCredentials: true });
      toast({ title: "Jardim adubado!", description: "Seu jardim ganhou +3 pontos coletivos 🌿" });
      fetchData();
    } catch (err) {
      toast({ title: "Erro", description: err.response?.data?.detail || "Erro ao adubar.", variant: "destructive" });
    } finally {
      setSelfFertilizing(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <LoadingAnimation size="md" />
      </div>
    );
  }

  if (!circle) {
    return <EmptyCircleState />;
  }

  const members = garden?.members || [];
  const collectivePts = garden?.collective_points || 0;
  const collectiveStage = collectiveStageFromPts(collectivePts);
  const activeFertilizers = resources?.active_fertilizers ?? 0;

  const otherMembers = members.filter((m) => m.user_id !== user?.user_id);
  const needyMembers = otherMembers.filter((m) =>
    ["yellowing", "drying"].includes(m.health_state)
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">

        {/* Circle Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm border border-purple-100 mb-2">
            <span className="text-purple-600 font-semibold text-sm">{circle.name || "Seu Círculo"}</span>
          </div>
          <p className="text-gray-500 text-sm">{members.length} integrantes</p>
        </div>

        {/* Resources */}
        {resources && (
          <div className="flex justify-center">
            <ResourceBar
              resources={resources}
              size="lg"
              className="bg-purple-600/90 border-purple-400/30"
            />
          </div>
        )}

        {/* Section 1: Garden — member acacia grid */}
        <section className="bg-white rounded-2xl shadow-sm border border-purple-50 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
            🌿 Jardim do Círculo
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {members.map((m) => {
              const activeIsland = m.active_island_id || 1;
              const pts = m.island_points || 0;
              const stage = stageFromPoints(pts);
              return (
                <div key={m.user_id} className="flex flex-col items-center gap-1">
                  <AcaciaTree
                    stage={stage}
                    islandId={activeIsland}
                    healthState={m.health_state || "healthy"}
                    size={70}
                  />
                  <span
                    className="text-xs text-gray-600 font-medium text-center max-w-[72px] truncate"
                    title={m.name}
                  >
                    {m.name?.split(" ")[0]}
                  </span>
                  <span className="text-xs text-gray-400">{pts}pts</span>
                </div>
              );
            })}
          </div>

          {/* Collective garden progress */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-gray-500 font-medium">Progresso coletivo</span>
              <span className="text-xs text-purple-600 font-semibold">
                Estágio {collectiveStage}/4 · {collectivePts}pts
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-400 to-violet-500 rounded-full transition-all duration-700"
                style={{ width: `${Math.min(100, (collectivePts / 100) * 100)}%` }}
              />
            </div>
          </div>
        </section>

        {/* Section 2: Member list */}
        <section className="bg-white rounded-2xl shadow-sm border border-purple-50 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            👥 Integrantes
          </h2>
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm">
                  {(m.name || "?")[0].toUpperCase()}
                </div>
                <span className="flex-1 text-sm text-gray-700 font-medium">
                  {m.name}
                  {m.user_id === user?.user_id && (
                    <span className="ml-1 text-xs text-gray-400">(você)</span>
                  )}
                </span>
                <HealthBadge state={m.health_state} />
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Eco Feed */}
        <section className="bg-white rounded-2xl shadow-sm border border-purple-50 p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            💬 Ecos recentes
          </h2>
          <EcoFeed echos={echos} loading={echoLoading} />
        </section>

        {/* Section 4: Actions */}
        <section className="space-y-3">
          {/* Self-water own tree */}
          <button
            onClick={handleSelfWater}
            disabled={selfWatering || !(resources?.waterings > 0)}
            className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
              resources?.waterings > 0
                ? "border-blue-200 bg-blue-50 hover:bg-blue-100"
                : "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
            }`}
          >
            <span className="text-2xl">💧</span>
            <div>
              <p className="font-semibold text-gray-800 text-sm">
                {selfWatering ? "Regando…" : "Regar minha acácia"}
              </p>
              <p className="text-xs text-gray-500">
                {resources?.waterings > 0 ? `Custa 1 💧 · ${resources.waterings} disponível(is)` : "Sem regas disponíveis"}
              </p>
            </div>
          </button>

          {/* Self-fertilize own garden */}
          <button
            onClick={handleSelfFertilize}
            disabled={selfFertilizing || activeFertilizers === 0}
            className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
              activeFertilizers > 0
                ? "border-green-200 bg-green-50 hover:bg-green-100"
                : "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
            }`}
          >
            <span className="text-2xl">🌱</span>
            <div>
              <p className="font-semibold text-gray-800 text-sm">
                {selfFertilizing ? "Adubando…" : "Adubar meu jardim"}
              </p>
              <p className="text-xs text-gray-500">
                {activeFertilizers > 0 ? `Custa 1 🌿 · ${activeFertilizers} disponível(is)` : "Sem adubos disponíveis"}
              </p>
            </div>
          </button>

          {/* Offer watering */}
          {otherMembers.length > 0 && (
            <button
              onClick={() => setShowWaterModal(true)}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
                needyMembers.length > 0
                  ? "border-blue-300 bg-blue-50 hover:bg-blue-100"
                  : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
              disabled={!resources?.watering_cans || !resources?.waterings}
            >
              <span className="text-2xl">🪣</span>
              <div>
                <p className="font-semibold text-gray-800 text-sm">Oferecer Rega</p>
                <p className="text-xs text-gray-500">
                  {needyMembers.length > 0
                    ? `${needyMembers.length} colega(s) precisam de rega`
                    : "Doe 1 🪣 + 1 💧 para uma colega"}
                </p>
              </div>
              {needyMembers.length > 0 && (
                <span className="ml-auto bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {needyMembers.length}
                </span>
              )}
            </button>
          )}

          {/* Send fertilizer */}
          <button
            onClick={async () => {
              await fetchCircleList();
              setShowFertModal(true);
            }}
            disabled={activeFertilizers === 0}
            className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 text-left transition-all ${
              activeFertilizers > 0
                ? "border-green-300 bg-green-50 hover:bg-green-100"
                : "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
            }`}
          >
            <span className="text-2xl">🌿</span>
            <div>
              <p className="font-semibold text-gray-800 text-sm">Enviar Adubo para outro Jardim</p>
              <p className="text-xs text-gray-500">
                {activeFertilizers > 0
                  ? `${activeFertilizers} adubo(s) disponível(is)`
                  : "Sem adubos disponíveis"}
              </p>
            </div>
          </button>
        </section>
      </div>

      {/* Naming modal */}
      {showNaming && (
        <CircleNaming
          circle={circle}
          onVoted={() => { setShowNaming(false); fetchData(); }}
          onClose={() => setShowNaming(false)}
        />
      )}

      {/* Watering selection modal */}
      {showWaterModal && (
        <SelectMemberModal
          title="Para quem quer enviar a rega?"
          subtitle="Doe 1 🪣 + 1 💧 · Máx 2 regas/dia"
          members={otherMembers}
          selected={wateringTarget}
          onSelect={setWateringTarget}
          onConfirm={handleWater}
          onClose={() => { setShowWaterModal(false); setWateringTarget(null); }}
          confirming={watering}
          confirmLabel="Enviar Rega 💧"
        />
      )}

      {/* Fertilizer target modal */}
      {showFertModal && (
        <SelectCircleModal
          circles={circleList}
          selected={fertTarget}
          onSelect={setFertTarget}
          onConfirm={handleFertilize}
          onClose={() => { setShowFertModal(false); setFertTarget(null); }}
          confirming={fertilizing}
        />
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */

function EmptyCircleState() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-6">
      <div className="text-center max-w-xs">
        <div className="text-6xl mb-4">🌱</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Você ainda não tem um círculo</h2>
        <p className="text-gray-500 text-sm">
          Aguarde a sua gestora criar e atribuir você a um círculo para começar a jornada coletiva.
        </p>
      </div>
    </div>
  );
}

function HealthBadge({ state }) {
  const config = {
    healthy: { label: "Saudável", cls: "bg-green-100 text-green-700" },
    thirsty: { label: "Com sede", cls: "bg-yellow-100 text-yellow-700" },
    yellowing: { label: "Amarelando", cls: "bg-orange-100 text-orange-700" },
    drying: { label: "Secando", cls: "bg-red-100 text-red-700" },
    dormant: { label: "Dormindo", cls: "bg-gray-100 text-gray-500" },
  };
  const { label, cls } = config[state] || config.healthy;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
  );
}

function SelectMemberModal({ title, subtitle, members, selected, onSelect, onConfirm, onClose, confirming, confirmLabel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-bold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="px-5 pb-3 space-y-2 max-h-52 overflow-y-auto">
          {members.map((m) => (
            <button
              key={m.user_id}
              onClick={() => onSelect(m)}
              className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                selected?.user_id === m.user_id
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-200 hover:border-blue-200"
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                {(m.name || "?")[0].toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-700 flex-1">{m.name}</span>
              <HealthBadge state={m.health_state} />
            </button>
          ))}
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!selected || confirming}
            className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-50"
          >
            {confirming ? "Enviando..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function SelectCircleModal({ circles, selected, onSelect, onConfirm, onClose, confirming }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-bold text-gray-800">Para qual jardim enviar o adubo?</h3>
          <p className="text-xs text-gray-500 mt-0.5">O destinatário saberá o nome do seu círculo, mas não quem enviou.</p>
        </div>
        <div className="px-5 pb-3 space-y-2 max-h-52 overflow-y-auto">
          {circles.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum outro círculo disponível</p>
          ) : (
            circles.map((c) => (
              <button
                key={c.circle_id}
                onClick={() => onSelect(c)}
                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                  selected?.circle_id === c.circle_id
                    ? "border-green-400 bg-green-50"
                    : "border-gray-200 hover:border-green-200"
                }`}
              >
                <span className="text-xl">🌿</span>
                <span className="text-sm font-medium text-gray-700">{c.name}</span>
              </button>
            ))
          )}
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!selected || confirming}
            className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 disabled:opacity-50"
          >
            {confirming ? "Enviando..." : "Enviar Adubo 🌿"}
          </button>
        </div>
      </div>
    </div>
  );
}
